import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Truck, Package, Home, Calendar, ArrowLeft } from 'lucide-react';
import { useUser } from '../context/UserContext';
import SEO from '../components/SEO';
import { buildApiUrl } from '../config';
import { OrderStatusBar } from '../components/OrderStatusBar';

const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { token } = useUser();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !id) return;
    
    window.scrollTo(0, 0);
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const res = await fetch(buildApiUrl(`/api/users/me/orders/${id}`), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await res.json();
        
        if (res.ok) {
          setOrder(data.order);
        } else {
          setError(data.message || 'Failed to load order');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load order');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id, token]);

  if (loading) {
    return (
      <div className="min-h-screen pt-8 px-4 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen pt-8 px-4 max-w-4xl mx-auto">
        <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
          <p className="text-red-500 mb-4">{error || 'Order not found'}</p>
          <Link to="/profile" className="text-primary-600 hover:text-primary-700 font-medium">
            &larr; Back to Profile
          </Link>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'text-green-700 bg-green-100';
      case 'failed': return 'text-red-700 bg-red-100';
      default: return 'text-blue-700 bg-blue-100';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-8 pb-12">
      <SEO 
        title={`Order ${order.razorpayOrderId || order._id} - c nine`} 
        description="Order details and tracking" 
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/profile" className="inline-flex items-center text-gray-500 hover:text-primary-600 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Orders
        </Link>

        {/* Success Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 mb-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
          <p className="text-gray-500 mb-6">Thank you for shopping with us. Your order has been successfully placed.</p>
          <div className="inline-flex items-center justify-center bg-gray-50 px-4 py-2 rounded-lg border border-gray-200 mb-8">
            <span className="text-gray-500 mr-2">Order ID:</span>
            <span className="font-mono font-bold text-gray-900">{order.razorpayOrderId || order._id}</span>
          </div>

          <div className="max-w-3xl mx-auto border-t border-gray-100 pt-8">
            <OrderStatusBar 
              status={order.status} 
              trackingStatus={order.trackingStatus} 
              createdAt={order.createdAt}
              etd={order.etd}
              awbCode={order.awbCode}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Main Order Details (Left 2 cols) */}
          <div className="md:col-span-2 space-y-6">
            
            {/* Items */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center border-b pb-4">
                <Package className="w-5 h-5 mr-2 text-primary-600" />
                Items Ordered
              </h2>
              <div className="space-y-4">
                {order.items.map((item: any) => {
                  const variantImage = item.selectedColor && item.productId?.colorVariants?.find((v: any) => v.name === item.selectedColor)?.image;
                  const itemImg = variantImage || item.productId?.image || 'https://via.placeholder.com/150';

                  return (
                    <div key={item._id} className="flex space-x-4 items-center">
                      <img 
                        src={itemImg} 
                        alt={item.productId?.title} 
                        className="w-16 h-16 rounded-lg object-cover border border-gray-100 flex-shrink-0"
                      />
                      <div className="flex-1">
                        <h3 className="text-gray-900 font-medium line-clamp-1">{item.productId?.title || 'Product'}</h3>
                        {item.selectedColor && (
                          <span className="inline-block text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded mt-0.5">
                            Color: {item.selectedColor}
                          </span>
                        )}
                        <p className="text-sm text-gray-500 mt-0.5">Qty: {item.quantity}</p>
                      </div>
                      <div className="font-bold text-gray-900">
                        ₹{item.priceAtPurchase * item.quantity}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tracking info */}
            {order.shiprocketOrderId && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center border-b pb-4">
                  <Truck className="w-5 h-5 mr-2 text-primary-600" />
                  Tracking Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Status</p>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold capitalize ${getStatusColor(order.status)}`}>
                      {order.trackingStatus || 'Processing'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">AWB Code</p>
                    <p className="font-mono font-medium text-gray-900">{order.awbCode || 'Pending Assignment'}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-sm text-gray-500 mb-1">Estimated Delivery</p>
                    <div className="flex items-center text-gray-900 font-medium">
                      <Calendar className="w-4 h-4 mr-2 text-primary-600" />
                      {order.etd ? new Date(order.etd).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'Will be updated upon courier pickup'}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            
            {/* Payment Summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 border-b pb-4">Payment Summary</h2>
              <div className="space-y-2 text-sm text-gray-600 mb-4">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>₹{order.amount}</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span className="text-green-600">Free</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t text-sm">
                  <span className="text-gray-500">Method</span>
                  <span className="font-semibold text-gray-900">
                    {order.paymentMethod === 'COD' ? 'Cash on Delivery' : 'Online Payment'}
                  </span>
                </div>
                <div className="border-t pt-2 mt-2 flex justify-between font-bold text-gray-900 text-lg">
                  <span>Total</span>
                  <span>₹{order.amount}</span>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            {order.shippingAddress && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center border-b pb-4">
                  <Home className="w-5 h-5 mr-2 text-primary-600" />
                  Delivery Address
                </h2>
                <div className="text-sm text-gray-600 space-y-1">
                  <p className="font-medium text-gray-900">{order.shippingAddress.firstName} {order.shippingAddress.lastName}</p>
                  <p>{order.shippingAddress.street}</p>
                  <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zip}</p>
                  <p>{order.shippingAddress.country}</p>
                  <p className="pt-2">Phone: {order.shippingAddress.phone}</p>
                </div>
              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
};

export default OrderDetailPage;
